import React from 'react'
// libs
import styled from 'styled-components'
// hooks
import { useCurrentForm, useCurrentTable } from 'hooks/risks'
import { useColumnsHeight, useGroupingTree } from '../hooks'
// components
import Rows from '../Rows'
import FormGroup from './components/FormGroup'
import AddRowButton from '../AddRowButton'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { FieldArray } from 'redux-form'
// views
import { OuterWrapper } from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Table/components/TableForm/components/Rows/components/Row/views'
// utils
import { isEmpty } from 'lodash'
import { getRowColor } from '../utils'
import { findPrevColumns } from '../../utils'

const GroupedColumnWrapper = styled.div<{ $mb: number; $mt?: number }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: margin-bottom 150ms ease;
  margin-top: ${({ $mt }) => `${$mt}px`};
  margin-bottom: ${({ $mb }) => `${$mb}px`};

  & > ${OuterWrapper} {
    padding: 4px;
  }
`

interface Props {
  parentId: string
  subParentId?: string
}

/** Render content (column) for fieldLink which grouped by another fieldLink or !fieldLink.primary */

const GroupedColumn: React.FC<Props> = ({ parentId, subParentId }) => {
  const { columnHeights, groupingMap } = useCurrentTable()
  const { fieldLink, primaryFieldLink } = useCurrentForm()

  const { isRisksRegister } = React.useContext(RiskTemplateAPIContext)

  const tree = useGroupingTree(parentId, subParentId)

  const isMultiGroup = primaryFieldLink.fieldGroups.length > 1
  const columnId = isMultiGroup ? `${fieldLink.id}_${subParentId || parentId}` : fieldLink.id

  const { ref } = useColumnsHeight(fieldLink, columnId)

  const offset = React.useMemo(() => {
    const rootId = tree.fieldLinkId

    if (!rootId) {
      return { bottom: 0, top: 0 }
    }

    const root = columnHeights[rootId]
    const current = columnHeights[columnId]

    const bottomOffset = root?.height - current?.height

    let topOffset = 0

    if (!isMultiGroup) {
      const prevColumns = findPrevColumns(columnHeights, groupingMap, rootId)

      if (prevColumns?.length) {
        topOffset = prevColumns.reduce((current, next) => {
          return current + next.height
        }, 0)
      }
    }

    return { bottom: bottomOffset, top: topOffset }
  }, [columnHeights, tree.fieldLinkId, fieldLink.id, isMultiGroup, groupingMap])

  return (
    <GroupedColumnWrapper ref={ref} $mb={offset.bottom} $mt={offset.top}>
      {!isRisksRegister && !tree.rows.length && <AddRowButton disabled canGenerate={false} newRecordLabel={fieldLink.addFieldLabel} />}

      {tree.rows.map(([fieldValueId, level], idx) =>
        isEmpty(level?.rows) ? (
          <FieldArray
            component={Rows}
            key={fieldValueId}
            bg={getRowColor(idx)}
            parentId={fieldValueId}
            disabled={parentId !== tree.fieldLinkId}
            name={`${fieldLink.id}[${fieldValueId}]`}
          />
        ) : (
          <FormGroup
            level={level}
            key={fieldValueId}
            groupedBy={parentId}
            bg={getRowColor(idx)}
            parentId={fieldValueId}
          />
        )
      )}
    </GroupedColumnWrapper>
  )
}

export default GroupedColumn
